import { FlagBag, FlagBagStatus } from "services/api/flagbags/types";
import { flagBagApi } from "services/api/flagbags";

const isMissingObjectStoreError = (error: unknown): boolean => {
	const message = error instanceof Error ? error.message : String(error ?? "");
	return (
		message.includes("objectStore does not exists:") ||
		message.includes("One of the specified object stores was not found")
	);
};

/**
 * Get all flagbags - if online, load from API and update cache; if offline, load from cache sorted by updatedAt desc
 * @param flagBagDB - IndexedDB instance for flagbag
 * @param stationId - Station ID (required for API calls)
 * @param isOnline - Whether the app is online (default: false)
 * @param eventId - Event ID (required if online)
 * @returns Promise<FlagBag[]> - Array of flagbags
 */
export const getAllFlagbags = async (
	flagBagDB: any,
	stationId?: string,
	isOnline: boolean = false,
	eventId?: string,
	search?: string
): Promise<FlagBag[]> => {
	try {
		if (isOnline) {
			// Online: Load all from API, then update local cache
			if (!eventId || !stationId) {
				console.warn("getAllFlagbags: Missing required parameters for API call", { eventId, stationId });
				// Fallback to cache if parameters missing
				const cachedFlagbags = (await flagBagDB.getAll()) as FlagBag[];
				return filterFlagbagsByKeyword(processCachedFlagbags(cachedFlagbags, stationId), search);
			}
			
			try {
				// Fetch all flagbags from API. Search is applied locally so station/finalStation fields
				// and cached display names can be matched consistently.
				const responseData = await flagBagApi.getFlagBagList(eventId, stationId, 1, 9000);
				const flagBags = responseData.items;
				
				// Update local cache with API data
				const updatePromises = flagBags.map((flagBag: FlagBag) => {
					return flagBagDB.update(flagBag);
				});
				await Promise.all(updatePromises);
				
				// Filter by stationId if provided
				let result = flagBags;
				if (stationId) {
					result = flagBags.filter((flagbag: FlagBag) => (flagbag.stationId === stationId || flagbag.finalStationId === stationId));
				}
				
				// Remove duplicates and sort by lastUpdatedAt desc
				const uniqueFlagbags = Array.from(
					new Map(result.map((fb: FlagBag) => [fb.id, fb])).values()
				);
				
				return filterFlagbagsByKeyword(sortByUpdatedAtDesc(uniqueFlagbags), search);
			} catch (error: any) {
				console.error("getAllFlagbags: API error, falling back to cache", error);
				// Fallback to cache on API error
				const cachedFlagbags = (await flagBagDB.getAll()) as FlagBag[];
				return filterFlagbagsByKeyword(processCachedFlagbags(cachedFlagbags, stationId), search);
			}
		} else {
			// Offline: Load all from cache, sort by updatedAt desc
			const cachedFlagbags = (await flagBagDB.getAll()) as FlagBag[];
			return filterFlagbagsByKeyword(processCachedFlagbags(cachedFlagbags, stationId), search);
		}
	} catch (error) {
		if (isMissingObjectStoreError(error)) {
			console.warn("getAllFlagbags: IndexedDB object store missing, returning empty cache result.", error);
			return [];
		}
		console.error("getAllFlagbags: Error fetching flagbags", error);
		throw error;
	}
};

/**
 * Process cached flagbags: filter by stationId, remove duplicates, and sort by updatedAt desc
 */
const processCachedFlagbags = (flagbags: FlagBag[], stationId?: string): FlagBag[] => {
	let filteredFlagbags = flagbags;
	
	if (stationId) {
		filteredFlagbags = flagbags.filter(
			(flagbag: FlagBag) => flagbag.stationId === stationId || flagbag.finalStationId === stationId
		);
	}
	
	// Remove duplicates based on flagbag ID
	const uniqueFlagbags = Array.from(
		new Map(filteredFlagbags.map((fb: FlagBag) => [fb.id, fb])).values()
	);
	
	return sortByUpdatedAtDesc(uniqueFlagbags);
};

const filterFlagbagsByKeyword = (flagbags: FlagBag[], search?: string): FlagBag[] => {
	const keyword = search?.trim().toLowerCase();
	if (!keyword) {
		return flagbags;
	}

	return flagbags.filter((flagbag: FlagBag) => {
		const fb = flagbag as any;
		const searchableValues = [
			flagbag.flagbagNum,
			fb.flagBagNum,
			flagbag.volunteerName,
			fb.volunteerEmail,
			fb.email,
			flagbag.id,
			flagbag.stationName,
			fb.stationName,
			flagbag.finalStationName,
			fb.finalStationNum,
			flagbag.finalStationId,
		];

		return searchableValues.some((value) =>
			String(value ?? "").toLowerCase().includes(keyword)
		);
	});
};

/**
 * Sort flagbags by lastUpdatedAt descending
 */
const sortByUpdatedAtDesc = (flagbags: FlagBag[]): FlagBag[] => {
	return [...flagbags].sort((a, b) => {
		const dateA = a.lastUpdatedAt ? new Date(a.lastUpdatedAt).getTime() : 0;
		const dateB = b.lastUpdatedAt ? new Date(b.lastUpdatedAt).getTime() : 0;
		return dateB - dateA; // Descending order (newest first)
	});
};

